import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Trash2, Trophy, Clock, Users, RefreshCw, Plus, X, Loader2, Hash } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

function useCountdown(endsAt) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endsAt) - Date.now();
      if (diff <= 0) return setRemaining('Ended');
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (d > 0) setRemaining(`${d}d ${h}h ${m}m`);
      else if (h > 0) setRemaining(`${h}h ${m}m ${s}s`);
      else setRemaining(`${m}m ${s}s`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [endsAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return remaining;
}

function CreateGiveawayModal({ guildId, onClose, onCreated }) {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [prize, setPrize] = useState('');
  const [minutes, setMinutes] = useState(60);
  const [winners, setWinners] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    axios.get(`${API}/api/announce/channels/${guildId}`, authHeader())
      .then(r => {
        if (!mounted.current) return;
        const chans = r.data.channels || [];
        setChannels(chans);
        if (chans.length > 0) setChannelId(chans[0].id);
      })
      .catch(() => setError('Failed to load channels.'))
      .finally(() => mounted.current && setLoadingChannels(false));
    return () => { mounted.current = false; };
  }, [guildId]);

  const submit = async () => {
    if (!prize.trim() || !channelId || minutes < 1) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/giveaways`, {
        guild_id: guildId,
        channel_id: channelId,
        prize: prize.trim(),
        minutes: Number(minutes),
        winners: Number(winners),
      }, authHeader());
      onCreated(data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start giveaway.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-2xl p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg flex items-center gap-2">
            <Gift size={18} className="text-[#5865F2]" /> Start a Giveaway
          </h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Prize</label>
            <input
              type="text"
              value={prize}
              onChange={e => setPrize(e.target.value)}
              placeholder="e.g. Discord Nitro"
              maxLength={200}
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#5865F2]/50"
            />
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Channel</label>
            {loadingChannels ? (
              <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm py-2.5"><Loader2 size={14} className="animate-spin" /> Loading channels...</div>
            ) : (
              <div className="relative">
                <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <select
                  value={channelId}
                  onChange={e => setChannelId(e.target.value)}
                  className="w-full appearance-none bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl pl-8 pr-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] focus:outline-none focus:border-[#5865F2]/50"
                >
                  {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Duration (min)</label>
              <input
                type="number"
                min={1}
                max={1440}
                value={minutes}
                onChange={e => setMinutes(e.target.value)}
                className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] focus:outline-none focus:border-[#5865F2]/50"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Winners</label>
              <input
                type="number"
                min={1}
                max={10}
                value={winners}
                onChange={e => setWinners(e.target.value)}
                className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] focus:outline-none focus:border-[#5865F2]/50"
              />
            </div>
          </div>
        </div>

        {error && <p className="text-red-400 text-xs mt-3">{error}</p>}

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-sm font-[Manrope] transition-colors">Cancel</button>
          <button
            onClick={submit}
            disabled={!prize.trim() || !channelId || loading || loadingChannels}
            className="flex-1 py-2.5 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />}
            Start Giveaway
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function GiveawayCard({ giveaway, onDelete }) {
  const countdown = useCountdown(giveaway.ends_at);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl flex-shrink-0">🎉</span>
          <div className="min-w-0">
            <p className="text-[var(--text-primary)] font-semibold font-[Outfit] truncate">{giveaway.prize}</p>
            <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Hosted by {giveaway.hosted_by}</p>
          </div>
        </div>
        {!giveaway.ended && (
          <button
            onClick={() => onDelete(giveaway.id)}
            className="flex-shrink-0 text-[var(--text-muted)] hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-500/10"
            title="Cancel giveaway"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
          <Users size={14} className="text-[#5865F2]" />
          <span className="font-[Manrope]">{giveaway.winner_count} winner{giveaway.winner_count !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
          <Clock size={14} className="text-[#5865F2]" />
          <span className="font-[Manrope]">{countdown}</span>
        </div>
      </div>

      {giveaway.ended && giveaway.winners && giveaway.winners.length > 0 && (
        <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <Trophy size={14} className="text-amber-400 flex-shrink-0" />
          <p className="text-amber-300 text-xs font-[Manrope]">
            {giveaway.winners.length === 1 ? 'Winner' : 'Winners'}: {giveaway.winners.join(', ')}
          </p>
        </div>
      )}
      {giveaway.ended && (!giveaway.winners || giveaway.winners.length === 0) && (
        <div className="flex items-center gap-2 bg-zinc-500/10 border border-zinc-500/20 rounded-lg px-3 py-2">
          <Trophy size={14} className="text-zinc-500 flex-shrink-0" />
          <p className="text-zinc-500 text-xs font-[Manrope]">No valid entrants</p>
        </div>
      )}
    </motion.div>
  );
}

export default function Giveaways() {
  const { selectedGuildId } = useGuild();
  const [active, setActive] = useState([]);
  const [ended, setEnded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchGiveaways = useCallback(async () => {
    if (!selectedGuildId) return;
    try {
      const token = localStorage.getItem('discord_token');
      const r = await axios.get(`${API}/api/giveaways/${selectedGuildId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActive(r.data.active || []);
      setEnded(r.data.ended || []);
      setError(null);
    } catch (e) {
      setError('Failed to load giveaways.');
    } finally {
      setLoading(false);
    }
  }, [selectedGuildId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    fetchGiveaways();
    const id = setInterval(fetchGiveaways, 30000);
    return () => clearInterval(id);
  }, [selectedGuildId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this giveaway?')) return;
    try {
      const token = localStorage.getItem('discord_token');
      await axios.delete(`${API}/api/giveaways/${selectedGuildId}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActive(prev => prev.filter(g => g.id !== id));
    } catch (e) {
      alert('Failed to cancel giveaway.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="giveaways-main">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-[var(--text-primary)] flex items-center gap-3">
              <Gift size={28} className="text-[#5865F2]" />
              Giveaways
            </h1>
            <p className="text-[var(--text-muted)] text-sm mt-1 font-[Manrope]">Manage active and recent giveaways</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={fetchGiveaways}
              className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)]"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors"
            >
              <Plus size={15} />
              Start Giveaway
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm font-[Manrope]">
            {error}
          </div>
        )}

        <div className="max-w-3xl space-y-8">
          {/* Active Giveaways */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <h2 className="text-[var(--text-secondary)] text-sm font-semibold font-[Manrope] uppercase tracking-wider">
                Active ({active.length})
              </h2>
            </div>
            <AnimatePresence mode="popLayout">
              {active.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center"
                >
                  <Gift size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
                  <p className="text-[var(--text-muted)] font-[Manrope] text-sm">No active giveaways</p>
                  <p className="text-[var(--text-muted)] font-[Manrope] text-xs mt-1">Start one above, or use /giveaway in Discord</p>
                </motion.div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {active.map(g => (
                    <GiveawayCard
                      key={g.id}
                      giveaway={g}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </AnimatePresence>
          </section>

          {/* Ended Giveaways */}
          {ended.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-zinc-500" />
                <h2 className="text-[var(--text-secondary)] text-sm font-semibold font-[Manrope] uppercase tracking-wider">
                  Recent Ended ({ended.length})
                </h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 opacity-75">
                {ended.map(g => (
                  <GiveawayCard key={g.id} giveaway={g} onDelete={() => {}} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <AnimatePresence>
        {showCreate && (
          <CreateGiveawayModal
            guildId={selectedGuildId}
            onClose={() => setShowCreate(false)}
            onCreated={(g) => setActive(prev => [g, ...prev])}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
